import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { List } from 'antd';
import { Card, Button, ClassifyStructure, Col, Form, Input, InputNumber, MainModal, message, Modal, ProTableOt, Row, Select, Seperator, setParamsFilter, Tabs, Typography, useProTableOt, TreeSelect, Popconfirm } from './../../ui/ot'
import { usePosition } from '../controllers/position_controller';
import { useDepth } from '../controllers/depth_controller';
import { X } from 'lucide-react';
import axios from 'axios';


// 區域顏色對應 (隨區域ID變動)
const AREA_COLORS = [
    '#FF6F61', // 鮮橙色
    '#6FA3EF', // 天藍色
    '#FFB74D', // 柔和橙色
    '#4DB6AC', // 淺藍綠色
    '#FF8C94', // 淺紅色
    '#FFD54F', // 明亮黃色
    '#64B5F6', // 藍色
    '#81C784', // 淺綠色
    '#E57373', // 鮮紅色
    '#FFF176', // 柔和黃色
    '#F06292', // 粉紅色
    '#BA68C8', // 淺紫色
    '#64B6F7', // 淡藍色
    '#FFAB91', // 淡橙色
    '#B2EBF2', // 輕藍色
    '#C5E1A5', // 淺綠色
    '#FFB3E6', // 淡粉色
    '#B3E5FC', // 藍色
    '#FFCCBC', // 淺橙色
    '#D7CCC8', // 淺灰色
    '#D1C4E9', // 淺紫色
];


// === Venue Component (場地) ===
const Venue = (
    {
        propsForm,
        propsLoading = false,
    }
) => {
    const [form_] = Form.useForm()
    const form = propsForm ?? form_
    const [formChildren] = Form.useForm()
    const [venueData, setVenueData] = useState({ width: 0, length: 0, capacity: 1 });
    const [grid, setGrid] = useState([]);
    const [selectedSlots, setSelectedSlots] = useState([]);
    const [areas, setAreas] = useState([]);
    const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);
    const [editingArea, setEditingArea] = useState(null);
    const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [startDrag, setStartDrag] = useState({ row: null, col: null });
    const [activeKey, setActiveKey] = useState()
    const [loading, setLoading] = useState(false)

    const handleFinish = (values) => {
        setVenueData(values);
        generateGrid(values.width, values.length, values.capacity, values?.areas);
        setIsVenueModalOpen(false);
    };

    const generateGrid = (width, length, capacity, areas = []) => {
        const slots = Array.from({ length: width * length }, (_, idx) => ({
            id: idx,
            area: areas?.find(item => item?.property_json?.slots?.includes(idx))?.position_id ?? null,
            capacity: capacity,
            usedCapacity: 0,
            x: idx % width, // Calculate the x-coordinate (column)
            y: Math.floor(idx / width), // Calculate the y-coordinate (row)
        }));
        setGrid(slots);
    };

    // 在 toggleSlotSelection 中添加檢查已分配區域的邏輯
    const toggleSlotSelection = (slotId) => {
        const slot = grid[slotId];
        // 確保未分配區域的格子才能選擇
        if (slot.area === null) {
            setSelectedSlots((prev) =>
                prev.includes(slotId)
                    ? prev.filter((id) => id !== slotId)
                    : [...prev, slotId]
            );
        }
    };
    const handleMouseDown = (idx) => {
        setIsDragging(true);
        toggleSlotSelection(idx);
    };

    // 在 handleMouseEnter 中也添加檢查
    const handleMouseEnter = (idx) => {
        const slot = grid[idx];
        // 確保未分配區域的格子才能選擇
        if (isDragging && slot.area === null) {
            toggleSlotSelection(idx);
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };
    const assignAreaToSlots = (values) => {
        const areaId = values?.position_id; // 輪流使用顏色
        const name = values?.position_language_data?.find(_ => true)?.language_value;
        const newGrid = grid.map((slot) =>
            selectedSlots.includes(slot.id) ? { ...slot, area: areaId } : slot
        );
        const points = grid.filter((slot) =>
            selectedSlots.includes(slot.id)
        );
        // Assuming the `slots` array is generated as per the previous example
        const minX = Math.min(...points.map(slot => slot.x));
        const minY = Math.min(...points.map(slot => slot.y));
        const maxX = Math.max(...points.map(slot => slot.x));
        const maxY = Math.max(...points.map(slot => slot.y));

        // Calculate the width and height
        const row = maxX - minX + 1;
        const column = maxY - minY + 1;

        setAreas([...areas, {
            id: areaId,
            position_id: areaId,
            name: name,
            name_language_data: [{
                language_culture_code: "zh-tw",
                language_culture_value: name,
            }],
            slots: selectedSlots, property_json: { slots: selectedSlots, points }, row, column
        }]);
        form.setFieldsValue({
            children: [...areas, {
                id: areaId,
                position_id: areaId,
                name: name,
                name_language_data: [{
                    language_culture_code: "zh-tw",
                    language_culture_value: name,
                }],
                slots: selectedSlots, property_json: { slots: selectedSlots, points }, row, column
            }]
        })
        setGrid(newGrid);
        setSelectedSlots([]);
        message.success(depthDataName + '已成功分配');
    };

    const editArea = (area) => {
        setEditingArea(area);
        setSelectedSlots(area.slots);
        setIsAreaModalOpen(true);
        formChildren.setFieldsValue(area)
        formChildren.setFieldsValue({ 'PositionData': [area] })
    };

    const saveEditedArea = () => {
        const newGrid = grid.map((slot) => {
            if (editingArea.slots.includes(slot.id)) {
                return { ...slot, usedCapacity: slot.usedCapacity }; // Keep the used capacity as is
            }
            return slot;
        });

        setGrid(newGrid);
        setAreas((prev) =>
            prev.map((area) => (area.id === editingArea.id ? { ...area, name: editingArea.name } : area))
        );
        setEditingArea(null);
        setSelectedSlots([]);
        setIsAreaModalOpen(false);
        message.success('區域已更新');
    };

    const handleOnRemove = (areaId) => {
        const newGrid = grid.map((slot) =>
            slot?.area === areaId ? { ...slot, area: null } : slot
        );
        setGrid(newGrid);
        setAreas(pre => pre?.filter((area) => area.id !== areaId))
        deletePositionData([{ position_id: areaId }])
    }

    const handleOnPost = () => {
        let newId;
        const usedIds = new Set(areas.map(area => area.id)); // Store existing area IDs in a set for faster lookup

        do {
            newId = Math.floor(Math.random() * 10000); // Generate a random number for the ID (you can set the range as needed)
        } while (usedIds.has(newId)); // Keep generating if the ID already exists

        const points = grid.filter((slot) =>
            selectedSlots.includes(slot.id)
        );
        // Assuming the `slots` array is generated as per the previous example
        const minX = Math.min(...points.map(slot => slot.x));
        const minY = Math.min(...points.map(slot => slot.y));
        const maxX = Math.max(...points.map(slot => slot.x));
        const maxY = Math.max(...points.map(slot => slot.y));

        // Calculate the width and height
        const row = maxX - minX + 1;
        const column = maxY - minY + 1;
        const height = venueData?.capacity

        const values = form.getFieldsValue();
        const formValues = {
            classify_structure_type_id: values?.classify_structure_type_id,
            name: `${depthDataName} ${newId}`,
            name_language_data: [{
                language_culture_code: "zh-tw",
                language_culture_value: `${depthDataName} ${newId}`,
            }],
            slots: selectedSlots, property_json: { slots: selectedSlots, points }, row, column, height
        }
        postPositionData([formValues])
    }

    const updateSlotUsedCapacity = (slotId, value) => {
        const newGrid = grid.map((slot) => {
            if (slot.id === slotId) {
                return { ...slot, usedCapacity: Math.max(0, Math.min(slot.capacity, value)) };
            }
            return slot;
        });
        setGrid(newGrid);
    };

    const { width, length, capacity } = venueData;

    const {
        PositionData,
        getPositionData,
        postPositionData,
        deletePositionData
    } = usePosition({
        onBefore: ({ axiosMode }) => {
            if (axiosMode === "post") {
                setLoading(true)
            } else if (axiosMode === "delete") {
                setLoading(true)
            }
        },
        onFinish: ({ axiosMode, status, values }) => {
            if (status === "success") {
                if (axiosMode === "post") {
                    assignAreaToSlots(values)
                    setLoading(false)
                } else if (axiosMode === "delete") {
                    message.success('刪除成功')
                    setLoading(false)
                }
            } else {
                if (axiosMode === "post") {
                    setLoading(false)
                } else if (axiosMode === "delete") {
                    message.success('刪除失敗')
                    setLoading(false)
                }
            }

        }
    });

    const {
        DepthData,
        getDepthData
    } = useDepth({});

    const treeData = Form.useWatch('PositionData', form) ?? [];
    const depth = Form.useWatch('depth', form) ?? [];
    const depth_data = DepthData?.find(item => item?.depth_index === depth + 1)
    const depthDataName = (
        depth_data?.name_language_data || []
    )?.find(
        (tmp) =>
            tmp?.language_culture_code ===
            "zh-tw"
    )?.language_culture_value || "-";

    const row = Form.useWatch('row', form) ?? 0;
    const column = Form.useWatch('column', form) ?? 0;
    const height = Form.useWatch('height', form) ?? 0;
    const children = Form.useWatch('children', form) ?? [];

    useEffect(() => {
        if (row !== 0 && column !== 0) {
            handleFinish({
                width: column,
                length: row,
                capacity: height,
                areas: children,
            })
        }
    }, [row, column, height])

    useEffect(() => {
        getDepthData()
    }, [])
    return (
        <Card title={venueData.name || '請選擇設定的區域'} bordered={true}>
            <Row gutter={[16, 16]}>
                <Col>
                    <Button type="primary" htmlType="submit" loading={propsLoading}>
                        儲存
                    </Button>
                </Col>
                <Col>
                    <TreeSelect
                        name={'position'}
                        value={[activeKey]}
                        multiple={false}
                        treeData={treeData}
                        treeDefaultExpandAll
                        onChange={(e, label, extra) => {
                            setActiveKey(e.target.value)
                            form.setFieldsValue(extra)
                            handleFinish({
                                length: extra?.row,
                                width: extra?.column,
                                capacity: extra?.height,
                                name: extra?.title,
                                areas: extra?.children
                            })
                            setAreas(extra?.children?.map(item => (
                                {
                                    ...item,
                                    id: item?.position_id,
                                    name: item?.title,
                                    slots: item?.property_json?.slots ?? []
                                }
                            )))
                        }}
                    />
                </Col>
            </Row>
            <Row gutter={[8, 8]}>
                <Col span={24}>
                    <p>尺寸：{width} x {length}</p>
                    <p>最大容量：{width * length}</p>
                    {/* <Button type="primary" onClick={() => setIsVenueModalOpen(true)}>
                        重新設定場地
                    </Button> */}
                    {depth_data && <Button
                        type="primary"
                        loading={loading}
                        onClick={handleOnPost}
                        style={{ marginLeft: '10px' }}
                        disabled={loading || selectedSlots.length === 0}
                    >
                        分配為同一區域
                    </Button>}
                </Col>
                <Col span={24}>
                    {
                        areas && <Row gutter={[8, 8]}>
                            {areas.map((area, index) => {
                                return (
                                    <Col xxl={6} xl={6} lg={8} md={8} sm={12} xs={24} xxs={24}>
                                        <Card
                                            title={<>
                                                <Row justify={'space-between'}>
                                                    <Col>
                                                        {area.name}
                                                    </Col>
                                                    <Col>
                                                        <Popconfirm description={`是否刪除此${area?.name}`} onConfirm={() => handleOnRemove(area?.id)}><X /></Popconfirm>
                                                    </Col>
                                                </Row>
                                            </>} styles={{ borderWidth: '6px', borderStyle: 'solid', borderColor: `${AREA_COLORS[area?.id % AREA_COLORS.length]}` }}
                                        >
                                            <Row>
                                                <Col span={24}>包含{area.slots.length} 個格子</Col>
                                                <Col span={24}>
                                                    <Button onClick={() => editArea(area)}>編輯區域</Button>
                                                </Col>
                                            </Row>
                                        </Card>
                                    </Col>
                                )
                            })
                            }
                        </Row>
                    }

                    {/* <List
                        header={<h3>目前區域列表</h3>}
                        bordered
                        dataSource={areas}
                        renderItem={(area) => (
                            <List.Item onClick={() => editArea(area)} style={{ cursor: 'pointer' }}>
                                <span>{area.name} - 包含 {area.slots.length} 個格子</span>
                            </List.Item>
                        )}
                        style={{ marginTop: '20px' }}
                    /> */}

                </Col>
                <Col span={24}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${width}, 1fr)`,
                            gap: '4px',
                            marginTop: '10px',
                            userSelect: 'none', // 添加此行以禁用文本選擇
                        }}
                        onMouseUp={handleMouseUp}
                    >
                        {grid.map((slot) => (
                            <div
                                key={slot.id}
                                style={{
                                    border: '1px solid #ccc',
                                    backgroundColor: selectedSlots.includes(slot.id)
                                        ? '#91d5ff'
                                        : slot.area !== null
                                            ? AREA_COLORS[slot.area % AREA_COLORS.length]
                                            : 'white',
                                    height: '50px',
                                    cursor: 'pointer',
                                    position: 'relative',
                                }}
                                onMouseDown={() => handleMouseDown(slot.id)}
                                onMouseEnter={() => handleMouseEnter(slot.id)}
                            >
                                <span style={{ position: 'absolute', top: '5px', left: '5px' }}>
                                    {slot.usedCapacity} / {slot.capacity}
                                </span>
                            </div>
                        ))}
                    </div>

                    <Modal
                        title="設定場地"
                        open={isVenueModalOpen}
                        onCancel={() => setIsVenueModalOpen(false)}
                        footer={null}
                    >
                        <Form onFinish={handleFinish}>
                            <Form.Item name="name" label="場地名稱" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="width" label="寬度 (單位格數)" rules={[{ required: true }]}>
                                <Input type="number" />
                            </Form.Item>
                            <Form.Item name="length" label="長度 (單位格數)" rules={[{ required: true }]}>
                                <Input type="number" />
                            </Form.Item>
                            <Form.Item name="capacity" label="每個格子容量" rules={[{ required: true }]}>
                                <Input type="number" min={1} />
                            </Form.Item>
                            <Button type="primary" htmlType="submit">
                                儲存
                            </Button>
                        </Form>
                    </Modal>

                    <Modal
                        title="編輯區域"
                        open={isAreaModalOpen}
                        onCancel={() => setIsAreaModalOpen(false)}
                        footer={null}
                        width={1000}
                    >
                        {DepthData?.find(item => item?.depth_index === depth + 2) === undefined && <Row gutter={[8, 8]}>
                            <Col span={24}>
                                <Input
                                    placeholder="區域名稱"
                                    value={editingArea?.name}
                                    onChange={(e) => setEditingArea({ ...editingArea, name: e.target.value })}
                                    style={{ marginBottom: '10px' }}
                                />
                            </Col>
                            {editingArea && editingArea?.slots.map((slotId) => {
                                console.log(slotId)
                                const slot = grid[slotId];
                                return (
                                    <Col span={24}>
                                        <Card>
                                            <Row gutter={[8, 8]}>
                                                <Col span={24}>
                                                    <span>格子{slotId + 1}</span>
                                                </Col>
                                                <Col span={24}>
                                                    <span>最大容量: {slot?.capacity}, 已存放: {slot?.usedCapacity}, 剩餘可放置: {slot?.capacity - slot?.usedCapacity}</span>
                                                </Col>
                                                <Col>
                                                    設定存放數量：
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        max={slot.capacity}
                                                        value={slot.usedCapacity}
                                                        onChange={(e) => updateSlotUsedCapacity(slotId, Number(e.target.value))}
                                                    // style={{ width: '100px', marginLeft: '10px' }}
                                                    />
                                                </Col>
                                            </Row>
                                        </Card>
                                    </Col>
                                )
                            })}
                            <Button onClick={saveEditedArea}>儲存編輯</Button>
                        </Row>}
                        {DepthData?.find(item => item?.depth_index === depth + 2) !== undefined && <Venue
                            propsForm={formChildren}
                        />}
                    </Modal>
                </Col>
            </Row>
        </Card>
    );
};

export default Venue;
